/**
 * Random-access chunks for Infinite Practice. Each chunk has its own seeded random source, so
 * overlapping windows recreate exactly the same obstacles without keeping the discarded course.
 * The dimensions match the title race's proven gaps, bars, vaults, slides, and low hurdles.
 * Generation happens between simulation ticks; the simulator still reads an ordinary static world.
 */
import type { CourseData, CourseEntity } from "@lache/sim";
import { Rng } from "./rng";

export const INFINITE_COURSE = "infinite";
export const INFINITE_TITLE = "Infinite Practice";
export const INFINITE_CHUNK_LENGTH = 24;
export const INFINITE_WINDOW_CHUNKS = 7;

/** Six metres of checkpoint run-up; the longest lane still leaves five metres of landing ground. */
const CHECKPOINT_X = 2;
const OBSTACLE_X = 8;
const BAR_SPACING = 3;
const BAR_HEIGHT = 3.8;

export type InfiniteSectionKind = "warmup" | "gap" | "lane" | "vault" | "slide" | "hurdle";

export interface InfiniteSection {
  /** Absolute chunk number; independent of the retained window. */
  index: number;
  kind: InfiniteSectionKind;
  /** Section boundaries in the returned course's local coordinates. */
  x: number;
  endX: number;
}

export interface InfiniteWindow {
  course: CourseData;
  firstChunk: number;
  chunkCount: number;
  /** Add this to local x to obtain distance in the full generated course. */
  originX: number;
  /** Right edge in local coordinates. The runtime extends the window well before reaching it. */
  endX: number;
  sections: InfiniteSection[];
}

const KINDS = [
  { weight: 3, value: "gap" },
  { weight: 4, value: "lane" },
  { weight: 3, value: "vault" },
  { weight: 2, value: "slide" },
  { weight: 1, value: "hurdle" },
] as const;

const SECTION_NAMES: Record<InfiniteSectionKind, string> = {
  warmup: "Warm-up",
  gap: "Jump",
  lane: "Laches",
  vault: "Vault",
  slide: "Slide",
  hurdle: "Hurdle",
};

/** Include both halves of the index, so the course does not repeat after 2^32 chunks. */
function chunkSeed(seed: number, index: number): number {
  let mixed = seed ^ Math.imul(index >>> 0, 0x9e3779b1);
  mixed ^= Math.imul(Math.floor(index / 0x100000000), 0x85ebca6b);
  mixed = Math.imul(mixed ^ (mixed >>> 16), 0x7feb352d);
  return (mixed ^ (mixed >>> 15)) >>> 0;
}

/**
 * At most seven chunks are retained, regardless of how far the player has travelled. Local
 * coordinates keep collision and drawing precision unchanged throughout a long practice session.
 */
export function generateInfiniteWindow(
  seed: number,
  firstChunk = 0,
  chunkCount = INFINITE_WINDOW_CHUNKS,
): InfiniteWindow {
  if (!Number.isInteger(seed)) throw new RangeError("Infinite seed must be an integer");
  if (
    !Number.isSafeInteger(firstChunk) ||
    firstChunk < 0 ||
    !Number.isSafeInteger((firstChunk + INFINITE_WINDOW_CHUNKS) * INFINITE_CHUNK_LENGTH)
  ) {
    throw new RangeError("Infinite chunk index must be a nonnegative safe integer");
  }
  if (!Number.isInteger(chunkCount) || chunkCount < 1 || chunkCount > INFINITE_WINDOW_CHUNKS) {
    throw new RangeError(`Infinite windows contain 1 to ${INFINITE_WINDOW_CHUNKS} chunks`);
  }

  const entities: CourseEntity[] = [];
  const respawns: CourseData["respawns"] = [];
  const sections: InfiniteSection[] = [];
  for (let offset = 0; offset < chunkCount; offset++) {
    const index = firstChunk + offset;
    const rng = new Rng(chunkSeed(seed, index));
    const kind: InfiniteSectionKind = index === 0 ? "warmup" : rng.pick(KINDS);
    const x = offset * INFINITE_CHUNK_LENGTH;
    const endX = x + INFINITE_CHUNK_LENGTH;
    const at = x + OBSTACLE_X;
    sections.push({ index, kind, x, endX });
    respawns.push({ x: x + CHECKPOINT_X, y: 0, name: `${index + 1}: ${SECTION_NAMES[kind]}` });

    if (kind === "gap" || kind === "lane") {
      const bars = kind === "lane" ? rng.int(2, 4) : 0;
      // The last bar sits a metre over the landing bank, giving early releases somewhere safe to land.
      // Binary-fraction gap widths rebase without rounding seams.
      const width = kind === "lane" ? BAR_SPACING * bars - 1 : rng.int(42, 52) / 16;
      entities.push(
        { type: "solid", x, y: -1, w: OBSTACLE_X, h: 1 },
        { type: "water", x: at, y: -2, w: width, h: 1 },
      );
      for (let bar = 0; bar < bars; bar++) {
        entities.push({ type: "bar", x: at + BAR_SPACING * (bar + 1), y: BAR_HEIGHT });
      }
      entities.push({ type: "solid", x: at + width, y: -1, w: endX - at - width, h: 1 });
    } else {
      entities.push({ type: "solid", x, y: -1, w: INFINITE_CHUNK_LENGTH, h: 1 });
      if (kind === "slide") {
        entities.push({ type: "pipe", x: at, y: 1.5, w: 2, h: 0.4 });
      } else {
        const height = kind === "vault" ? (rng.next() < 0.5 ? 1 : 1.2) : 0.4;
        entities.push({ type: "box", x: at, y: 0, w: kind === "vault" ? 1 : 0.8, h: height });
      }
    }
  }
  // A short level apron and wall keep a player walking backward out of discarded geometry.
  // They sit before the first checkpoint, outside every retained chunk's reproducible layout.
  entities.push({ type: "solid", x: -3, y: -1, w: 3, h: 1 }, { type: "solid", x: -4, y: -1, w: 1, h: 6 });
  return {
    course: {
      version: 1,
      name: INFINITE_COURSE,
      title: INFINITE_TITLE,
      start: { x: CHECKPOINT_X, y: 0 },
      respawns,
      marks: [],
      entities,
    },
    firstChunk,
    chunkCount,
    originX: firstChunk * INFINITE_CHUNK_LENGTH,
    endX: chunkCount * INFINITE_CHUNK_LENGTH,
    sections,
  };
}
